<template>
  <div>
    <h2>{{ orgName }} Organization</h2>
    <div class="search-container">
      <input
        v-model="orgUserSearchText"
        placeholder="Search users by name..."
        class="search-input"
      />
    </div>
    <div v-if="loading" class="spinner-container">
      <div class="spinner"></div>
    </div>
    <ul v-if="filteredOrgUsers.length">
      <li v-for="user in filteredOrgUsers" :key="user.id">
        <div class="user-card">
          <UserCard :user="user" />
          <div class="user-actions-inline">
            <button
              v-if="user.id !== user.organization_owner_id"
              class="btn-add"
              style="margin-right: auto"
              @click="makeUserOwner(user.auth0_user_id)"
            >
              Make Owner
            </button>
            <button
              v-if="user.id !== user.organization_owner_id"
              @click="removeUserFromOrg(user.auth0_user_id)"
              :disabled="actionLoadingIds.has(user.auth0_user_id)"
              class="btn-remove"
            >
              <span v-if="actionLoadingIds.has(user.auth0_user_id)"
                >Removing...</span
              >
              <span v-else>Remove</span>
            </button>
          </div>
        </div>
      </li>
    </ul>
    <div v-else-if="!loading" style="margin: 1rem 0">
      {{ orgUserSearchText ? 'No users found matching your search.' : 'No users found for this organization.' }}
    </div>
  </div>
  <div>
    <button @click="addUserModalVisible = true">Add Users</button>
  </div>
  <div v-if="addUserModalVisible" class="modal-overlay">
    <div class="modal">
      <h3>Add User to {{ orgName }}</h3>
      <input
        v-model="userSearchText"
        placeholder="Search users by validation info"
        class="search-input"
        @keyup.enter="getUsers"
      />
      <div v-if="loading" class="spinner-container">
        <div class="spinner"></div>
      </div>
      <ul v-if="userSearchResults.length">
        <li v-for="user in userSearchResults" :key="user.id">
          <div class="user-card">
            <UserCard :user="user" />
            <div class="user-actions-inline">
              <button
                v-if="!addedUserIds.has(user.auth0_user_id)"
                @click="addUserToOrg(user.auth0_user_id)"
                :disabled="actionLoadingIds.has(user.auth0_user_id)"
                class="btn-add"
              >
                <span v-if="actionLoadingIds.has(user.auth0_user_id)"
                  >Adding...</span
                >
                <span v-else>Add</span>
              </button>
              <div v-else class="added-pill">Added ✓</div>
            </div>
          </div>
        </li>
      </ul>
      <div
        v-else-if="!loading && hasSearched && userSearchResults.length === 0"
      >
        No users found.
      </div>
      <button @click="closeModal" style="margin-top: 1rem">Close</button>
    </div>
  </div>
</template>
<script setup>
import { onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import { useAdminApi } from "@/plugins/api";
import UserCard from "@/components/UserCard.vue";
const $adminApi = useAdminApi();
const route = useRoute();

const orgName = route.params.organizationName;
const orgId = route.query.organizationId;
const orgUsers = ref([]);
const loading = ref(false);
const addUserModalVisible = ref(false);
const userSearchText = ref("");
const userSearchResults = ref([]);
const hasSearched = ref(false);
const actionLoadingIds = ref(new Set());
const addedUserIds = ref(new Set());
const orgUserSearchText = ref("");

const orderedOrgUsers = computed(() => {
  return orgUsers.value.slice().sort((a, b) => {
    // Owner always comes first
    const aIsOwner = a.id === a.organization_owner_id;
    const bIsOwner = b.id === b.organization_owner_id;
    
    if (aIsOwner && !bIsOwner) return -1;
    if (!aIsOwner && bIsOwner) return 1;
    
    // If neither or both are owners, sort alphabetically by first name
    return a.first_name.localeCompare(b.first_name);
  });
});

const filteredOrgUsers = computed(() => {
  const searchTerm = orgUserSearchText.value.toLowerCase().trim();

  if (!searchTerm) {
    return orderedOrgUsers.value;
  }

  return orderedOrgUsers.value.filter((user) => {
    const firstName = user.first_name?.toLowerCase() || "";
    const lastName = user.last_name?.toLowerCase() || "";
    const fullName = `${firstName} ${lastName}`;

    return (
      firstName.includes(searchTerm) ||
      lastName.includes(searchTerm) ||
      fullName.includes(searchTerm)
    );
  });
});

async function getOrgUsers() {
  try {
    loading.value = true;
    const users = await $adminApi.getUsersByOrganizationId(orgId);
    orgUsers.value = users;
  } catch (error) {
    console.error("Error fetching organization users:", error);
  } finally {
    loading.value = false;
  }
}

async function getUsers() {
  try {
    loading.value = true;
    const users = await $adminApi.getUsers(userSearchText.value);
    hasSearched.value = true;
    userSearchResults.value = users;
  } catch (error) {
    console.error("Error fetching organization users:", error);
  } finally {
    loading.value = false;
  }
}

async function addUserToOrg(userauth0Id) {
  if (actionLoadingIds.value.has(userauth0Id)) return;
  try {
    actionLoadingIds.value.add(userauth0Id);
    await $adminApi.addUserToOrganization(userauth0Id, orgId);
    addedUserIds.value.add(userauth0Id);
    await getOrgUsers();
  } catch (error) {
    console.error("Error adding user to organization:", error);
  } finally {
    actionLoadingIds.value.delete(userauth0Id);
  }
}
async function removeUserFromOrg(userauth0Id) {
  if (actionLoadingIds.value.has(userauth0Id)) return;
  try {
    actionLoadingIds.value.add(userauth0Id);
    await $adminApi.removeUserFromOrganization(userauth0Id);
    orgUsers.value = orgUsers.value.filter(
      (user) => user.auth0_user_id !== userauth0Id
    );
    addedUserIds.value.delete(userauth0Id);
  } catch (error) {
    console.error("Error removing user from organization:", error);
  } finally {
    actionLoadingIds.value.delete(userauth0Id);
  }
}

async function makeUserOwner(userauth0Id) {
  if (actionLoadingIds.value.has(userauth0Id)) return;
  try {
    actionLoadingIds.value.add(userauth0Id);
    await $adminApi.updateUserToOwnerForOrganization(userauth0Id, orgId);
    await getOrgUsers();
  } catch (error) {
    console.error("Error making user owner of organization:", error);
  } finally {
    actionLoadingIds.value.delete(userauth0Id);
  }
}

function closeModal() {
  addUserModalVisible.value = false;
  userSearchText.value = "";
  userSearchResults.value = [];
  hasSearched.value = false;
}

onMounted(async () => {
  await getOrgUsers();
});
</script>
<style lang="css" scoped>
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  margin-bottom: 1rem;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background: var(--bg2);
  padding: 2rem;
  border-radius: 8px;
  min-width: 300px;
  color: var(--fg2);
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.search-container {
  margin: 1rem 0;
}
.search-input {
  padding: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: var(--bg2);
  color: var(--fg2);
}
ul {
  list-style: none;
  padding: 0;
  max-height: 40rem;
  overflow-y: auto;
  width: 100%;
}
li {
  background: var(--bg2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  display: flex;
  justify-content: center;
  margin: 1rem 0;
}

.user-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.user-actions-inline {
  display: flex;
  justify-content: flex-end;
  padding: 0 0.9rem 0.5rem;
}
.btn-add,
.btn-remove {
  font-size: 0.75rem;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
